import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import {
  ClipboardDocumentIcon,
  CheckIcon,
  UserGroupIcon,
  BriefcaseIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import { referralUrlForLink } from '../../utils/referralLinks';
import ShareKitQuickBar from './ShareKitQuickBar';
import Button from '../ui/Button';

const PROGRAM_STYLES = {
  clients: {
    border: 'border-orange-200/50',
    qrFg: '#c2410c',
    activeText: 'text-orange-700',
    icon: UserGroupIcon,
  },
  partners: {
    border: 'border-violet-200/60',
    qrFg: '#5b21b6',
    activeText: 'text-violet-700',
    icon: BriefcaseIcon,
  },
};

function ClientQuickInviteCard({
  link,
  secondaryLink,
  onOpenReferrals,
  labels,
  secondaryLabels,
  layout = 'vertical',
  compact = false,
  className = '',
  variant = 'clients',
  qrSize: qrSizeProp,
}) {
  const { t, formatMessage } = useLanguage();
  const primaryVariant = variant === 'partners' ? 'partners' : 'clients';
  const dualVariant = primaryVariant === 'partners' ? 'clients' : 'partners';
  const isDual = Boolean(secondaryLink);
  const [activeVariant, setActiveVariant] = useState(primaryVariant);
  const currentVariant = isDual ? activeVariant : primaryVariant;

  const homeFor = (key) => (key === 'partners' ? t.enrola.partnerHome : t.enrola.clientHome);
  const labelsFor = (key) => {
    if (key === primaryVariant) return labels || homeFor(key);
    if (key === dualVariant) return secondaryLabels || homeFor(key);
    return homeFor(key);
  };
  const linkFor = (key) => (key === primaryVariant ? link : secondaryLink);

  const h = labelsFor(currentVariant);
  const currentLink = linkFor(currentVariant);
  const programStyle = PROGRAM_STYLES[currentVariant];
  const hasLink = Boolean(currentLink?.link_code || currentLink?.code);
  const isHorizontal = layout === 'horizontal';

  const fullUrl = hasLink ? referralUrlForLink(currentLink) : '';
  const shareMessage = hasLink
    ? formatMessage(
        currentVariant === 'partners' ? t.enrola.defaultSharePartners : t.enrola.defaultShareClients,
        { url: fullUrl }
      )
    : '';

  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await copyTextToClipboard(fullUrl);
      setCopied(true);
      toast.success(t.enrola.linkCopied);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t.enrola.copyFailed);
    }
  };

  const qrSize = qrSizeProp || (isHorizontal ? 140 : (compact ? 160 : 120));

  const switchPill = isDual ? (
    <div className="px-3 pt-3">
      <div className="relative grid grid-cols-2 overflow-hidden rounded-full ring-1 ring-black/5">
        {['clients', 'partners'].map((key) => {
          const style = PROGRAM_STYLES[key];
          const Icon = style.icon;
          const isActive = currentVariant === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveVariant(key)}
              aria-pressed={isActive}
              className={`relative flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors duration-300 ${
                isActive
                  ? key === 'partners'
                    ? 'bg-violet-600 text-white'
                    : 'bg-orange-500 text-white'
                  : 'bg-white text-secondary-600 hover:text-secondary-900'
              }`}
            >
              <motion.span
                animate={{ scale: isActive ? 1.05 : 1 }}
                transition={{ duration: 0.25, ease: [0.34, 1.56, 0.64, 1] }}
                className="relative flex items-center justify-center gap-1.5"
              >
                <Icon className="h-4 w-4" />
                <span className="truncate">
                  {key === 'partners' ? t.enrola.inviteSwitchPartners : t.enrola.inviteSwitchClients}
                </span>
              </motion.span>
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const copyButton = (
    <motion.button
      type="button"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={copyLink}
      className={`inline-flex w-full items-center justify-center gap-2 rounded-xl py-2 text-sm font-semibold transition-colors ${
        copied
          ? 'bg-success-50 text-success-700  '
          : 'bg-secondary-100 text-secondary-800 hover:bg-orange-50 hover:text-orange-700    '
      }`}
    >
      {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
      {copied ? t.enrola.copiedShort : t.enrola.copyLink}
    </motion.button>
  );

  const shareRow = (
    <div
      className={`flex items-center justify-center rounded-xl bg-secondary-50/90  ${
        compact ? 'px-2.5 py-1.5' : 'px-3 py-2.5'
      }`}
    >
      <ShareKitQuickBar url={fullUrl} message={shareMessage} />
    </div>
  );

  const qrBlock = (
    <div
      className={`flex flex-col items-center ${
        isHorizontal ? 'gap-1.5' : compact ? 'flex-1 justify-center gap-2 py-1' : 'gap-2'
      }`}
    >
      <div className="rounded-xl border border-secondary-100 bg-white p-2.5 shadow-soft  ">
        <QRCodeSVG
          value={fullUrl}
          size={qrSize}
          level="H"
          includeMargin
          bgColor="#ffffff"
          fgColor={programStyle.qrFg}
        />
      </div>
      <p className="max-w-[11rem] text-center text-[10px] font-medium leading-snug text-secondary-500 ">
        {h.scanToJoin}
      </p>
    </div>
  );

  if (!hasLink) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.06 }}
        className={`card-premium flex flex-col overflow-hidden border ${programStyle.border} ${className}`}
      >
        {switchPill}
        <div className="p-6 text-center">
          <p className="text-sm text-secondary-600 ">{h.noInviteLink}</p>
          {onOpenReferrals ? (
            <Button onClick={onOpenReferrals} size="compact" className="mt-4 w-full">
              {h.openReferrals}
            </Button>
          ) : null}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.06 }}
      className={`card-premium flex flex-col overflow-hidden border ${programStyle.border}  ${className}`}
    >
      {switchPill}

      {isHorizontal ? (
        <div className="grid flex-1 grid-cols-1 items-center gap-3 p-4 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-4">
          {qrBlock}
          <div className="flex min-w-0 flex-col gap-2.5">
            {copyButton}
            {shareRow}
            {onOpenReferrals ? (
              <button
                type="button"
                onClick={onOpenReferrals}
                className={`inline-flex items-center justify-center gap-1 text-xs font-medium ${programStyle.activeText}`}
              >
                {h.openReferrals}
                <ArrowRightIcon className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={`flex flex-1 flex-col ${compact ? 'gap-2 p-3' : 'gap-2.5 p-3'}`}>
          {qrBlock}
          {copyButton}
          {shareRow}
          {onOpenReferrals ? (
            <button
              type="button"
              onClick={onOpenReferrals}
              className="text-center text-xs font-medium text-orange-600 hover:text-orange-700 "
            >
              {h.openReferrals}
            </button>
          ) : null}
        </div>
      )}
    </motion.div>
  );
}

export default ClientQuickInviteCard;
